import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { Button, ModalShell } from '@/components/ui';
import {
  getSavedConfigurations,
  deleteConfiguration,
  type SavedConfiguration,
} from '../../utils/configStorage';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { useModalStore } from '@/store/modalStore';
import { SensorType } from '@packages/types/device/lite/sensors';
import { getSensorTypeInfo } from '@packages/types/device/lite/lite_constraints';
import { MINI_SENSOR_TYPE_INFO, MINI_SENSOR_TYPE_MAP } from '@packages/types/device/mini/sensors';
import { DeviceType } from '@packages/types/device/common/deviceTypes';

// ── List-specific styled components ──────────────────────────────────────────

const ConfigList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]}px;
`;

const ConfigItem = styled.div<{ isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[3]}px ${theme.spacing[4]}px`};
  border: 2px solid
    ${({ theme, isSelected }) =>
      isSelected ? theme.colors.border.group.brand : theme.colors.border.container};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  background-color: ${({ theme, isSelected }) =>
    isSelected
      ? theme.colors.surface.interactive.selectedContent
      : theme.colors.surface.control.default};
  transition: all 0.2s;
  min-height: 60px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.border.group.brand};
    background-color: ${({ theme, isSelected }) =>
      isSelected
        ? theme.colors.surface.interactive.selectedContent
        : theme.colors.surface.interactive.hover};
  }
`;

const ConfigInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]}px;
`;

const ConfigName = styled.div`
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  word-break: break-word;
`;

const ConfigMeta = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;
`;

const ConfigActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]}px;
  align-items: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => `${theme.spacing[10]}px ${theme.spacing[5]}px`};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const ScrollBody = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 200px;
  max-height: calc(80vh - 220px);

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.surface.layout.background};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.container};
    border-radius: 4px;
    &:hover {
      background: ${({ theme }) => theme.colors.text.secondary};
    }
  }
`;

// ── Props ─────────────────────────────────────────────────────────────────────

interface ConfigListModalProps {
  isVisible: boolean;
  /** @deprecated Use deviceType instead */
  isMini?: boolean;
  deviceType?: DeviceType;
  onSelect: (config: SavedConfiguration) => void | Promise<void>;
  onClose: () => void;
  onDelete?: (id: string) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ConfigListModal: React.FC<ConfigListModalProps> = ({
  isVisible,
  isMini,
  deviceType: deviceTypeProp,
  onSelect,
  onClose,
  onDelete,
}) => {
  const { t } = useTranslation('config');
  const { showConfirm } = useModalStore();
  const [configs, setConfigs] = useState<SavedConfiguration[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const effectiveDeviceType: DeviceType =
    deviceTypeProp ?? (isMini ? DeviceType.MINI : DeviceType.LITE);

  useEffect(() => {
    if (isVisible) {
      const saved = getSavedConfigurations(effectiveDeviceType);
      setConfigs(saved);
      setSelectedId(null);
    }
  }, [isVisible, effectiveDeviceType]);

  const handleSelect = (config: SavedConfiguration) => {
    setSelectedId(config.id);
  };

  const handleConfirm = () => {
    if (selectedId) {
      const selected = configs.find(c => c.id === selectedId);
      if (selected) {
        onSelect(selected);
        onClose();
      } else {
        console.error('❌ 선택된 설정을 찾을 수 없음:', selectedId);
      }
    } else {
      console.warn('⚠️ 설정이 선택되지 않음');
    }
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    showConfirm(
      t(i18nKeys.config.delete),
      t(i18nKeys.config.msg_confirmDeleteConfig),
      () => {
        if (deleteConfiguration(id, effectiveDeviceType)) {
          setConfigs(prev => prev.filter(c => c.id !== id));
          if (selectedId === id) {
            setSelectedId(null);
          }
          onDelete?.(id);
        }
      },
      undefined,
      'danger'
    );
  };

  const formatDate = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      const year = date.getFullYear().toString().slice(-2);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}.${month}.${day} ${hours}:${minutes}`;
    } catch {
      return timestamp;
    }
  };

  const formatDeviceType = (config: SavedConfiguration): string => {
    const dt = config.configData.deviceType;
    if (!dt) return 'Unknown';

    if (dt === DeviceType.MINI) return 'MINI';
    if (dt === DeviceType.LITE) {
      const sensorTypes = config.configData.deviceConfig?.sensor_type as
        | Array<{ value: number }>
        | undefined;
      const sensorCount =
        sensorTypes?.filter(s => s && s.value !== undefined && s.value !== 255).length ?? 0;
      return sensorCount > 0 ? `LITE ${sensorCount}Gas` : 'LITE';
    }

    if (typeof dt === 'object') {
      if ((dt as any).isMini) return 'MINI';
      if ((dt as any).isLite) {
        const sensorTypes = config.configData.deviceConfig?.sensor_type as
          | Array<{ value: number }>
          | undefined;
        const sensorCount =
          sensorTypes?.filter(s => s && s.value !== undefined && s.value !== 255).length ?? 0;
        return sensorCount > 0 ? `LITE ${sensorCount}Gas` : 'LITE';
      }
    }

    return 'Unknown';
  };

  const formatSensorType = (config: SavedConfiguration): string => {
    const dt = config.configData.deviceType;
    if (!dt) return 'Unknown';

    const isMiniConfig =
      dt === DeviceType.MINI || (typeof dt === 'object' && (dt as any).isMini === true);

    if (isMiniConfig) {
      const equippedSensor = config.configData.deviceInfo?.equipped_sensor;
      if (equippedSensor !== undefined && equippedSensor !== null) {
        let sensorTypeValue: number;
        if (typeof equippedSensor === 'string') {
          sensorTypeValue = MINI_SENSOR_TYPE_MAP[equippedSensor] ?? -1;
        } else if (typeof equippedSensor === 'number') {
          sensorTypeValue = equippedSensor;
        } else {
          return 'Unknown';
        }

        if (sensorTypeValue >= 0 && sensorTypeValue in MINI_SENSOR_TYPE_INFO) {
          const sensorInfo =
            MINI_SENSOR_TYPE_INFO[sensorTypeValue as keyof typeof MINI_SENSOR_TYPE_INFO];
          return sensorInfo?.label || `Sensor ${sensorTypeValue}`;
        }
      }
      return 'Unknown';
    } else {
      const sensorTypes = config.configData.deviceConfig?.sensor_type as
        | Array<{ value: number; label: string }>
        | undefined;

      if (sensorTypes && Array.isArray(sensorTypes)) {
        const actualSensors = sensorTypes
          .filter(s => s && s.value !== undefined && s.value !== 255)
          .map(s => {
            const sensorInfo = getSensorTypeInfo(s.value as SensorType);
            return sensorInfo?.label || s.label || `Sensor ${s.value}`;
          });
        return actualSensors.length > 0 ? actualSensors.join(', ') : 'No Sensors';
      }
      return 'Unknown';
    }
  };

  return (
    <ModalShell
      isOpen={isVisible}
      title={t(i18nKeys.config.savedConfigurations)}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t(i18nKeys.config.cancel)}
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={!selectedId}>
            {t(i18nKeys.config.load)}
          </Button>
        </>
      }
    >
      <ScrollBody>
        {configs.length === 0 ? (
          <EmptyState>{t(i18nKeys.config.noSavedConfigurations)}</EmptyState>
        ) : (
          <ConfigList>
            {configs.map(config => (
              <ConfigItem
                key={config.id}
                isSelected={selectedId === config.id}
                onClick={() => handleSelect(config)}
              >
                <ConfigInfo>
                  <ConfigName>{config.name}</ConfigName>
                  <ConfigMeta>
                    {formatDeviceType(config)} • {formatSensorType(config)} •{' '}
                    {formatDate(config.timestamp)}
                  </ConfigMeta>
                </ConfigInfo>
                <ConfigActions>
                  <Button variant="secondary" onClick={e => handleDelete(e, config.id)}>
                    {t(i18nKeys.config.delete)}
                  </Button>
                </ConfigActions>
              </ConfigItem>
            ))}
          </ConfigList>
        )}
      </ScrollBody>
    </ModalShell>
  );
};
